// Inventory & Assets — Phase 3 tabs: Exit Handover, Reports, Audit Log.

import { ViewSwitch } from "@/components/foodcost/FoodCostUI";
import { ReportBlocks } from "@/components/inventory/InvUI";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "../../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, Lock, Unlock, Camera, FileText,
         FileSpreadsheet, ClipboardList, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const post = async (path, token, body = {}) => {
  const r = await fetch(`${API}/api/inventory${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...body }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};

const money = (v) => `₹${Number(v || 0).toLocaleString(undefined,
  { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;


/* ============================================================
 * Exit Handover
 * ============================================================ */
export function ExitPanel({ token, center, isAdmin, isSuperAdmin }) {
  const [snaps, setSnaps] = useState([]);
  const [status, setStatus] = useState({ frozen: false, detail: {} });
  const [loading, setLoading] = useState(false);
  const [taking, setTaking] = useState(false);
  const [confirmFreeze, setConfirmFreeze] = useState(false);
  const [viewSnap, setViewSnap] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        post("/exit/status", token, { center }),
        post("/exit/snapshots", token, { center }),
      ]);
      setStatus(s);
      setSnaps(l.snapshots || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, center]);
  useEffect(() => { load(); }, [load]);

  const snapshot = async () => {
    setTaking(true);
    try {
      const r = await post("/exit/snapshot", token, { center,
        remarks: `Manual snapshot ${new Date().toISOString().slice(0, 19)}` });
      toast.success(`Snapshot ${r.snapshot.snapshot_id} — ${r.snapshot.asset_count} assets @ ${money(r.snapshot.totals.value)}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setTaking(false); }
  };

  const freeze = async () => {
    try {
      await post("/exit/freeze", token, { center,
        reason: "Franchise exit — manual freeze" });
      toast.success(`${center} is now FROZEN`);
      setConfirmFreeze(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const unfreeze = async () => {
    if (!window.confirm(`Unfreeze ${center}?`)) return;
    try {
      await post("/exit/unfreeze", token, { center });
      toast.success("Unfrozen");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4" data-testid="inv-exit-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Exit Handover — {center}
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            When a franchise exits, take a <b>Snapshot</b> of the current inventory value,
            then <b>Freeze</b> the centre to prevent any further edits. Only Super Admin can Unfreeze.
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              {status.frozen
                ? <Badge variant="destructive" data-testid="inv-exit-status"><Lock className="w-3 h-3 mr-1" />FROZEN</Badge>
                : <Badge variant="outline" data-testid="inv-exit-status"><Unlock className="w-3 h-3 mr-1" />Active</Badge>}
              {status.frozen && status.detail?.frozen_at && (
                <span className="text-xs text-muted-foreground">
                  since {status.detail.frozen_at.slice(0, 19)} by {status.detail.frozen_by}
                </span>
              )}
            </div>
            <Button size="sm" onClick={snapshot} disabled={taking} data-testid="inv-exit-snapshot">
              {taking && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              <Camera className="w-4 h-4 mr-1" /> Take Snapshot
            </Button>
            {isAdmin && !status.frozen && (
              <Button size="sm" variant="destructive" onClick={() => setConfirmFreeze(true)}
                data-testid="inv-exit-freeze">
                <Lock className="w-4 h-4 mr-1" /> Freeze Centre
              </Button>
            )}
            {isSuperAdmin && status.frozen && (
              <Button size="sm" variant="outline" onClick={unfreeze} data-testid="inv-exit-unfreeze">
                <Unlock className="w-4 h-4 mr-1" /> Unfreeze
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Snapshots</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Snapshot</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Franchise Exit</TableHead>
              <TableHead className="text-right">Assets</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Raw Material Value</TableHead>
              <TableHead>By</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {snaps.map(s => (
                <TableRow key={s.snapshot_id} data-testid={`inv-snap-row-${s.snapshot_id}`}>
                  <TableCell className="font-mono text-xs">{s.snapshot_id}</TableCell>
                  <TableCell className="text-xs">{(s.created_at || "").slice(0, 19)}</TableCell>
                  <TableCell>{s.franchise_exit_id || "—"}</TableCell>
                  <TableCell className="text-right">{s.asset_count}</TableCell>
                  <TableCell className="text-right font-semibold">
                    {money((s.totals || {}).value)}
                  </TableCell>
                  <TableCell className="text-right">
                    {money((s.totals || {}).raw_material_value)}
                  </TableCell>
                  <TableCell className="text-xs">{s.created_by}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline"
                      onClick={() => setViewSnap(s.snapshot_id)}
                      data-testid={`inv-snap-view-${s.snapshot_id}`}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && snaps.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                  No snapshots yet.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {confirmFreeze && (
        <Dialog open onOpenChange={(o) => !o && setConfirmFreeze(false)}>
          <DialogContent data-testid="inv-exit-freeze-dialog">
            <DialogHeader><DialogTitle>Freeze {center}?</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="p-3 rounded bg-red-500/10 text-sm">
                <ShieldAlert className="w-4 h-4 inline mr-1 text-red-500" />
                All asset edits, transfers, verifications and repairs will be blocked
                for this centre. A snapshot must be taken first if you haven&apos;t already.
              </div>
              <div>Only <b>Super Admin</b> can reverse this.</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmFreeze(false)}>Cancel</Button>
              <Button variant="destructive" onClick={freeze} data-testid="inv-exit-freeze-confirm">
                Yes, Freeze
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {viewSnap && <SnapshotDialog token={token} snapshotId={viewSnap}
        onClose={() => setViewSnap(null)} />}
    </div>
  );
}


function SnapshotDialog({ token, snapshotId, onClose }) {
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    post("/exit/snapshot/get", token, { snapshot_id: snapshotId })
      .then(d => setSnap(d.snapshot)).catch(e => toast.error(e.message));
  }, [token, snapshotId]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="inv-snap-dialog">
        <DialogHeader><DialogTitle>Snapshot {snapshotId}</DialogTitle></DialogHeader>
        {!snap ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              <div className="p-3 rounded bg-muted/50">
                <div className="text-xs uppercase text-muted-foreground">Total Assets</div>
                <div className="text-xl font-bold">{snap.asset_count}</div>
              </div>
              <div className="p-3 rounded bg-muted/50">
                <div className="text-xs uppercase text-muted-foreground">Total Value</div>
                <div className="text-xl font-bold">{money(snap.totals?.value)}</div>
              </div>
              <div className="p-3 rounded bg-muted/50">
                <div className="text-xs uppercase text-muted-foreground">Raw Material Value</div>
                <div className="text-xl font-bold">{money(snap.totals?.raw_material_value)}</div>
              </div>
              <div className="p-3 rounded bg-muted/50">
                <div className="text-xs uppercase text-muted-foreground">Quantity</div>
                <div className="text-xl font-bold">{snap.totals?.quantity}</div>
              </div>
            </div>
            <div>
              <div className="font-medium text-sm mb-2">By Category</div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {Object.entries(snap.totals_by_category || {}).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell>{k}</TableCell>
                      <TableCell className="text-right">{v.count}</TableCell>
                      <TableCell className="text-right">{v.qty}</TableCell>
                      <TableCell className="text-right">{money(v.value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================
 * Reports
 * ============================================================ */
export function ReportsPanel({ token, center }) {
  const [available, setAvailable] = useState([]);
  const [reportId, setReportId] = useState("");
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("both");

  useEffect(() => {
    post("/reports/available", token).then(d => {
      setAvailable(d.reports || []);
    }).catch(e => toast.error(e.message));
  }, [token]);
  useEffect(() => { setRep(null); }, [center]);

  const run = async (id = reportId) => {
    if (!id) return;
    setBusy(true);
    try {
      const d = await post("/reports/run", token, { report_id: id, center });
      setRep(d);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const download = async (format) => {
    if (!reportId) return;
    try {
      const r = await fetch(`${API}/api/inventory/reports/${format}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, report_id: reportId, center }),
      });
      if (!r.ok) throw new Error(`Export failed (HTTP ${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportId}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const pick = (id) => { setReportId(id); setRep(null); run(id); };

  return (
    <div className="space-y-4">
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-bold text-[#750817] flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          <FileText className="w-5 h-5" /> Inventory Reports</h2>
        <ViewSwitch view={view} setView={setView} testid="inv-rep-view" labels={["Report Blocks", "Report Grid", "Both"]} />
      </div>
      {view !== "grid" && <ReportBlocks reports={available} value={reportId} onPick={pick} />}
    </section>
    {view !== "cards" && <Card data-testid="inv-reports-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="w-4 h-4" /> {available.find(r => r.id === reportId)?.label || "Reports"}
        </CardTitle>
        <div className="text-sm text-muted-foreground">
          {available.length} inventory reports — preview in-app, then export to Excel or PDF.
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="min-w-[300px]">
            <Label className="text-xs">Report</Label>
            <Select value={reportId} onValueChange={pick}>
              <SelectTrigger data-testid="inv-reports-select"><SelectValue placeholder="Choose a report" /></SelectTrigger>
              <SelectContent>
                {available.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => run()} disabled={busy || !reportId} data-testid="inv-reports-run">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Run
          </Button>
          <Button variant="outline" onClick={() => download("excel")} disabled={!rep}
            data-testid="inv-reports-excel">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} disabled={!rep}
            data-testid="inv-reports-pdf">
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>

        {!reportId && !rep && (
          <div className="mt-4 rounded-xl border-2 border-dashed border-[#e2c27a] bg-[#fffaf0] p-6 text-center text-base text-[#5a3a22]" data-testid="inv-reports-empty">
            Select a report above to view its details.
          </div>
        )}
        {busy && !rep && <div className="mt-4 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {rep && (
          <div className="mt-4" data-testid="inv-reports-grid">
            <div className="text-sm mb-2">
              <b>{rep.title}</b> — {rep.count} row{rep.count !== 1 ? "s" : ""}
            </div>
            <div className="overflow-x-auto max-h-[500px] border rounded">
              <Table>
                <TableHeader><TableRow>
                  {rep.columns.map(c => <TableHead key={c}>{c}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {rep.rows.slice(0, 500).map((row, i) => (
                    <TableRow key={i}>
                      {row.map((v, j) => <TableCell key={j} className="text-xs">{v == null ? "—" : String(v)}</TableCell>)}
                    </TableRow>
                  ))}
                  {rep.rows.length === 0 && (
                    <TableRow><TableCell colSpan={rep.columns.length}
                      className="text-center text-muted-foreground py-6">
                      No data for this report.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              {rep.rows.length > 500 && (
                <div className="p-2 text-xs text-muted-foreground text-center">
                  Preview capped at 500 rows — use Excel / PDF for the full export ({rep.count} rows).
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>}
    </div>
  );
}


/* ============================================================
 * Audit Log
 * ============================================================ */
export function AuditPanel({ token, center }) {
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ action: "", asset_id: "",
                                             from_date: "", to_date: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { center, ...filters };
      Object.keys(q).forEach(k => { if (!q[k]) delete q[k]; });
      const d = await post("/audit/list-v2", token, q);
      setEvents(d.events || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, center, filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="inv-audit-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base">Audit Log</CardTitle>
          <div className="text-xs text-muted-foreground">
            Every mutation on inventory is logged — create, edit, invoice link, verification,
            transfer, repair, freeze.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input placeholder="asset_id" className="w-36" value={filters.asset_id}
            onChange={(e) => setFilters({ ...filters, asset_id: e.target.value })}
            data-testid="inv-audit-asset" />
          <Input placeholder="action" className="w-32" value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            data-testid="inv-audit-action" />
          <Input type="date" className="w-36" value={filters.from_date}
            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} />
          <Input type="date" className="w-36" value={filters.to_date}
            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} />
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-xs text-muted-foreground mb-2">{events.length} event(s)</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>Asset</TableHead>
            <TableHead>By</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Detail</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {events.map(e => (
              <TableRow key={e.event_id} data-testid={`inv-audit-row-${e.event_id}`}>
                <TableCell className="text-xs">{(e.timestamp || "").slice(0, 19)}</TableCell>
                <TableCell><Badge variant="outline">{e.action}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{e.asset_id}</TableCell>
                <TableCell>{e.by}</TableCell>
                <TableCell className="text-xs">{e.role || "—"}</TableCell>
                <TableCell className="text-xs">
                  {e.extra && Object.keys(e.extra).length > 0
                    ? JSON.stringify(e.extra).slice(0, 120)
                    : (e.reason || "—")}
                </TableCell>
              </TableRow>
            ))}
            {!loading && events.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                No events matched.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
